import { create } from 'zustand';
import { useModuleStore } from './moduleStore';
import { secureStorage } from '../lib/secureStorage';
import type {
  User,
  AuthResponse,
  CorePortalAuthResponse,
  LinkedTenant,
} from '../types/auth';

/**
 * Auth store — ports AuthService state from the Angular member portal.
 *
 * Storage strategy:
 *   - Access token   : in-memory (Zustand) only. Lost on app kill, refreshed on demand.
 *   - Refresh token  : secure storage (Keychain/Keystore on native; localStorage on web).
 *   - User profile   : localStorage (non-sensitive snapshot for offline first paint).
 *   - Tenant + URL   : localStorage (paas_tenant_id, paas_regional_api_url) — keys preserved
 *                      verbatim from Angular for cross-build interop.
 *   - SSO session    : secure storage (paas_sso_session_token).
 *   - Linked tenants : localStorage (paas_sso_tenants) — list, not credentials.
 *   - Active portal  : localStorage (paas_active_portal_domain) — last domain used after login
 *                      so dev refreshes don't re-resolve the SSO hub.
 *
 * Storage keys mirror Angular constants exactly.
 */

const ACCESS_TOKEN_KEY = 'paas_access_token';
const REFRESH_TOKEN_KEY = 'paas_refresh_token';
const USER_KEY = 'paas_user';
const SSO_SESSION_KEY = 'paas_sso_session_token';
const SSO_TENANTS_KEY = 'paas_sso_tenants';
const ACTIVE_PORTAL_DOMAIN_KEY = 'paas_active_portal_domain';
const TENANT_ID_KEY = 'paas_tenant_id';
const REGIONAL_API_URL_KEY = 'paas_regional_api_url';
const GUARD_API_KEY = 'guard_api_key';

export type GuardAuthMethod = 'user' | 'api_key' | null;

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  tenantId: string | null;
  regionalApiUrl: string;
  ssoSessionToken: string | null;
  linkedTenants: LinkedTenant[];
  apiKey: string | null;
  authMethod: GuardAuthMethod;

  isAuthenticated: () => boolean;
  isApiKeyAuth: () => boolean;

  /** Persist tokens + user after a successful auth response. */
  setAuth: (response: AuthResponse | CorePortalAuthResponse) => Promise<void>;

  setRegionalApiUrl: (url: string) => void;
  setTenantId: (tenantId: string | null) => void;
  setActivePortalDomain: (domain: string | null | undefined) => void;
  setSso: (response: CorePortalAuthResponse) => Promise<void>;
  setLinkedTenants: (tenants: LinkedTenant[]) => void;

  /** Kiosk / device API key (skips login UI). */
  setApiKeyAuth: (apiKey: string, tenantId: string) => Promise<void>;
  clearApiKeyAuth: () => Promise<void>;

  /** Clear everything — use on full sign-out. */
  signOut: () => Promise<void>;

  /** Read-only helpers for axios interceptors and guards. */
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  getApiKey: () => string | null;
  getTenantId: () => string | null;
  getRegionalApiUrl: () => string;

  /** Hydrate from storage on cold start. Idempotent. */
  hydrate: () => Promise<void>;
}

function readJSON<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function syncLicenseModules(user: User | null): void {
  useModuleStore.getState().syncLicensedModules(user?.license?.enabled_modules ?? []);
}

/** Map tenant API login user (`user_id`) into the canonical `User` shape. */
export function normalizeAuthUser(raw: User | null | undefined): User | null {
  if (!raw) return null;
  const userId = raw.userId ?? raw.user_id ?? '';
  const tenantId = raw.tenantId ?? raw.tenant_id ?? '';
  return {
    ...raw,
    userId,
    tenantId,
    user_id: raw.user_id ?? userId,
    tenant_id: raw.tenant_id ?? tenantId,
  };
}

/** Authenticated tenant user id (JWT `user_id` / store `userId`). */
export function getSessionUserId(): string | undefined {
  const user = normalizeAuthUser(useAuthStore.getState().user);
  if (user?.userId) return user.userId;

  const token = useAuthStore.getState().getAccessToken();
  if (!token) return undefined;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { user_id?: string };
    return payload.user_id;
  } catch {
    return undefined;
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  refreshToken: null,
  user: null,
  tenantId: null,
  regionalApiUrl: '',
  ssoSessionToken: null,
  linkedTenants: [],
  apiKey: null,
  authMethod: null,

  isAuthenticated() {
    const s = get();
    return !!(s.apiKey || s.accessToken || s.refreshToken);
  },

  isApiKeyAuth() {
    return get().authMethod === 'api_key' && !!get().apiKey;
  },

  async setAuth(response) {
    if (!response.success) return;
    const { accessToken, refreshToken, user: rawUser } = response;
    const user = normalizeAuthUser(rawUser);
    set({ accessToken, refreshToken, user });
    syncLicenseModules(user);

    // Persist non-access-token bits (refresh + user snapshot).
    try {
      // Mirror Angular: access token also kept in localStorage so legacy
      // helpers / interceptors still pick it up if invoked from contexts that
      // can't reach the in-memory store. Refresh token never goes here.
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch (error) {
      console.warn('[auth] persist user failed:', error);
    }
    if (refreshToken) {
      await secureStorage.set(REFRESH_TOKEN_KEY, refreshToken);
    }
  },

  setRegionalApiUrl(url) {
    set({ regionalApiUrl: url });
    if (url) {
      try { localStorage.setItem(REGIONAL_API_URL_KEY, url); } catch { /* noop */ }
    }
  },

  setTenantId(tenantId) {
    set({ tenantId });
    try {
      if (tenantId) localStorage.setItem(TENANT_ID_KEY, tenantId);
      else localStorage.removeItem(TENANT_ID_KEY);
    } catch { /* noop */ }
  },

  setActivePortalDomain(domain) {
    const d = domain?.trim();
    try {
      if (d) localStorage.setItem(ACTIVE_PORTAL_DOMAIN_KEY, d);
      else localStorage.removeItem(ACTIVE_PORTAL_DOMAIN_KEY);
    } catch { /* noop */ }
  },

  async setSso(response) {
    if (response.regional_api_base_url) {
      get().setRegionalApiUrl(response.regional_api_base_url);
    }
    if (response.tenant_id) {
      get().setTenantId(response.tenant_id);
    }
    if (response.domain) {
      get().setActivePortalDomain(response.domain);
    }

    const sessionToken = response.sso?.session_token;
    if (sessionToken) {
      set({ ssoSessionToken: sessionToken });
      await secureStorage.set(SSO_SESSION_KEY, sessionToken);
    }

    if (Array.isArray(response.sso?.tenants)) {
      const tenants = response.sso.tenants;
      set({ linkedTenants: tenants });
      try { localStorage.setItem(SSO_TENANTS_KEY, JSON.stringify(tenants)); } catch { /* noop */ }
    }
  },

  setLinkedTenants(tenants) {
    set({ linkedTenants: tenants });
    try { localStorage.setItem(SSO_TENANTS_KEY, JSON.stringify(tenants)); } catch { /* noop */ }
  },

  async setApiKeyAuth(apiKey, tenantId) {
    set({
      apiKey,
      authMethod: 'api_key',
      tenantId,
      accessToken: null,
      refreshToken: null,
      user: null,
    });
    try {
      localStorage.setItem(TENANT_ID_KEY, tenantId);
    } catch { /* noop */ }
    await secureStorage.set(GUARD_API_KEY, apiKey);
  },

  async clearApiKeyAuth() {
    set({ apiKey: null, authMethod: null });
    await secureStorage.remove(GUARD_API_KEY);
  },

  /**
   * Sign out — mirrors Angular `AuthService.logout()`: clear credentials only.
   * Keeps `paas_tenant_id`, `paas_regional_api_url`, and branding (same hostname).
   */
  async signOut() {
    const { tenantId, regionalApiUrl } = get();
    set({
      accessToken: null,
      refreshToken: null,
      user: null,
      tenantId,
      regionalApiUrl,
      ssoSessionToken: null,
      linkedTenants: [],
      apiKey: null,
      authMethod: null,
    });
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(SSO_TENANTS_KEY);
      localStorage.removeItem(ACTIVE_PORTAL_DOMAIN_KEY);
      // Intentionally keep TENANT_ID_KEY + REGIONAL_API_URL_KEY for login/public APIs.
    } catch { /* noop */ }
    await secureStorage.remove(REFRESH_TOKEN_KEY);
    await secureStorage.remove(SSO_SESSION_KEY);
    await secureStorage.remove(GUARD_API_KEY);
    syncLicenseModules(null);
  },

  getApiKey() { return get().apiKey; },

  getAccessToken() { return get().accessToken; },
  getRefreshToken() { return get().refreshToken; },
  getTenantId() { return get().tenantId; },
  getRegionalApiUrl() { return get().regionalApiUrl; },

  async hydrate() {
    if (typeof window === 'undefined') return;

    const accessToken = (() => {
      try { return localStorage.getItem(ACCESS_TOKEN_KEY); } catch { return null; }
    })();
    const userRaw = (() => {
      try { return localStorage.getItem(USER_KEY); } catch { return null; }
    })();
    const user = normalizeAuthUser(readJSON<User>(userRaw));
    const tenantId = (() => {
      try { return localStorage.getItem(TENANT_ID_KEY); } catch { return null; }
    })();
    const regionalApiUrl = (() => {
      try { return localStorage.getItem(REGIONAL_API_URL_KEY) ?? ''; } catch { return ''; }
    })();
    const linkedTenantsRaw = (() => {
      try { return localStorage.getItem(SSO_TENANTS_KEY); } catch { return null; }
    })();
    const linkedTenants = readJSON<LinkedTenant[]>(linkedTenantsRaw) ?? [];

    const refreshToken = await secureStorage.get(REFRESH_TOKEN_KEY);
    const ssoSessionToken = await secureStorage.get(SSO_SESSION_KEY);
    const apiKey = await secureStorage.get(GUARD_API_KEY);

    set({
      accessToken,
      refreshToken,
      user,
      tenantId,
      regionalApiUrl,
      ssoSessionToken,
      linkedTenants,
      apiKey: apiKey || null,
      authMethod: apiKey ? 'api_key' : accessToken || refreshToken ? 'user' : null,
    });
    syncLicenseModules(user);
  },
}));

export const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: ACCESS_TOKEN_KEY,
  REFRESH_TOKEN: REFRESH_TOKEN_KEY,
  USER: USER_KEY,
  SSO_SESSION: SSO_SESSION_KEY,
  SSO_TENANTS: SSO_TENANTS_KEY,
  ACTIVE_PORTAL_DOMAIN: ACTIVE_PORTAL_DOMAIN_KEY,
  TENANT_ID: TENANT_ID_KEY,
  REGIONAL_API_URL: REGIONAL_API_URL_KEY,
};
